import React from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { withRouter, Redirect } from 'react-router';
import { connect } from 'react-redux';
import i18n from 'util/i18n';
import env from 'config/environment';
import { api, getQueryValue } from 'services';
import * as errorCodes from 'constants/errorCodes';

import Logout from 'components/Form/Logout';
import { loginRequest } from '@redux/modules/authentication/actions';
import Bowser from 'bowser';
import Consent from 'components/Form/Consent';

export class Login extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      username: '',
      password: '',
      showPassword: false,
      samlData: {},
      samlError: null,
      saml2Data: {},
      saml2Error: null,
    };
  }

  initiateSaml(samlEndpoint, samlData, samlError, samlIdpName) {
    samlEndpoint()
      .then((response) => {
        const { data } = response;
        this.setState({ [samlData]: data });
      })
      .catch((e) => {
        console.warn(`${samlIdpName} SAML initialization failed`, e);
        // TODO - what are potential errors here?
        try {
          this.setState({
            [samlData]: {},
            [samlError]: i18n.t('login.error.saml', { identityProviderName: samlIdpName }),
          });
        } catch (err) {
          console.error(err);
        }
      });
  }

  UNSAFE_componentWillMount() {
    this.redirect();

    if (env.SamlEnabled()) {
      this.initiateSaml(api.saml.bind(api), 'samlData', 'samlError', env.SamlIdp1Name());
    }

    if (env.Saml2Enabled()) {
      this.initiateSaml(api.saml2.bind(api), 'saml2Data', 'saml2Error', env.SamlIdp2Name());
    }
  }

  onUsernameChange = (e) => { // eslint-disable-line react/sort-comp
    this.setState({ username: e.target.value });
  };

  onPasswordChange = (e) => {
    this.setState({ password: e.target.value });
  };

  togglePassword = () => {
    this.setState((prev) => ({
      showPassword: !prev.showPassword,
    }));
  };

  login = (event) => {
    const { dispatch } = this.props;
    const { username, password } = this.state;

    event.preventDefault();
    dispatch(loginRequest(username, password));
  };

  redirect() {
    const { history } = this.props;

    const err = getQueryValue(window.location.search, 'error');
    if (err) {
      switch (err) {
        case 'token':
          history.push('/token');
          return;
        case 'access_denied':
          history.push('/accessdenied');
          return;
        case 'saml_logout_failed':
          history.push('/error');
          break;
        default:
          history.push('/error');
          break;
      }
    }
  }

  errorMessage() {
    const { errors } = this.props;

    if (!errors || errors.length <= 0) {
      return null;
    }

    // Only show first error for now? UI TBD if multiple
    const [error] = errors;
    const { message, code } = error;

    let errorMessage;
    switch (code) {
      case errorCodes.USERNAME_MISSING:
      case errorCodes.PASSWORD_MISSING:
        errorMessage = i18n.t('login.error.generic');
        break;
      case errorCodes.NETWORK_ERROR:
        errorMessage = i18n.t('login.error.network');
        break;
      case errorCodes.UNKNOWN_ERROR:
        errorMessage = i18n.t('login.error.network', { code });
        break;
      default:
        errorMessage = message; // default to message sent by API
    }

    return this.renderLoginError(errorMessage);
  }

  renderLoginError = (error) => (
    <div className="field no-margin-bottom">
      <div className="table">
        <div className="usa-alert usa-alert-error" role="alert">
          <div className="usa-alert-body">
            <h5 className="usa-alert-heading">{i18n.t('login.error.title')}</h5>
            <p>{error}</p>
          </div>
        </div>
      </div>
    </div>
  );

  renderLoginAnnouncement = () => {
    let announcementTitle = i18n.t('login.announcement.title');
    let announcementBody = i18n.t('login.announcement.body');
    if ((typeof announcementTitle) === 'string' && announcementTitle.includes('LOGINANNOUNCEMENTTITLE')) {
      announcementTitle = '';
    } else {
      announcementTitle = i18n.m('login.announcement.title');
    }
    if ((typeof announcementBody) === 'string' && announcementBody.includes('LOGINANNOUNCEMENTBODY')) {
      announcementBody = '';
    } else {
      announcementBody = i18n.m('login.announcement.body');
    }
    if (announcementTitle || announcementBody) {
      return (
        <div style={{ textAlign: 'center', padding: '20px' }}>
          {announcementTitle}
          {announcementBody}
        </div>
      );
    }
  };

  renderLoginButton = (url, base64Xml, samlError, identityProviderName) => (
    <form method="get" action={url}>
      {base64Xml && <input type="hidden" name="SAMLRequest" value={base64Xml} />}

      {samlError && this.renderLoginError(samlError)}

      <button
        type="submit"
        className="usa-button-big"
        disabled={!url || !base64Xml}
      >
        <span>{i18n.t('login.saml.button', { identityProviderName })}</span>
      </button>
    </form>
  );

  renderSAMLAuth() {
    const {
      samlData: saml1Data, saml2Data, samlError: saml1Error, saml2Error
    } = this.state;
    const { URL: URL1 = '', Base64XML: Base64XML1 } = saml1Data;
    const { URL: URL2 = '', Base64XML: Base64XML2 } = saml2Data;

    return (
      <div id="saml" className="auth saml">
        {env.SamlEnabled() && this.renderLoginButton(URL1, Base64XML1, saml1Error, env.SamlIdp1Name())}

        {env.Saml2Enabled() && this.renderLoginButton(URL2, Base64XML2, saml2Error, env.SamlIdp2Name())}
      </div>
    );
  }

  renderBasicAuth() {
    const { errors } = this.props;
    const { username, password, showPassword } = this.state;
    const pwClass = classnames('password', 'help', {
      'usa-input-error': errors && errors.length,
    });

    const showPasswordButtonTitle = showPassword
      ? i18n.t('login.basic.hide.title')
      : i18n.t('login.basic.show.title');

    const showPasswordButtonText = showPassword
      ? i18n.t('login.basic.hide.text')
      : i18n.t('login.basic.show.text');

    return (
      <div id="basic" className="auth basic">
        <form onSubmit={this.login}>
          <div>
            <label htmlFor="user">{i18n.t('login.basic.username.label')}</label>
            <input
              id="user"
              name="user"
              type="text"
              aria-label="Enter email address as username"
              value={username}
              onChange={this.onUsernameChange}
            />
          </div>
          <div className={pwClass}>
            <label htmlFor="password">{i18n.t('login.basic.password.label')}</label>
            <input
              id="password"
              name="password"
              type={showPassword ? 'text' : 'password'}
              aria-label="Enter password"
              value={password}
              onChange={this.onPasswordChange}
            />
            <div className="peek">
              <button
                type="button"
                id="show-password"
                aria-label="Show password"
                onClick={this.togglePassword}
                title={showPasswordButtonTitle}
              >
                {showPasswordButtonText}
              </button>
            </div>
            {this.errorMessage()}
          </div>
          <div>
            <button type="submit" className="usa-button-big" aria-label="click to login">
              {i18n.t('login.basic.button')}
            </button>
            <a
              id="forgot-password"
              aria-label="click to reset password"
              href="#TODO"
              title={i18n.t('login.basic.forgot.title')}
            >
              {i18n.t('login.basic.forgot.text')}
            </a>
          </div>
        </form>
      </div>
    );
  }

  renderLogin() {
    const samlEnabled = env.SamlEnabled();
    const saml2Enabled = env.Saml2Enabled();
    const basicEnabled = env.BasicAuthenticationEnabled();

    const loginClasses = classnames('table', {
      two: (samlEnabled || saml2Enabled) && basicEnabled,
      one: ((samlEnabled || saml2Enabled) && !basicEnabled) || (!samlEnabled && !saml2Enabled && basicEnabled),
      zero: !samlEnabled && !saml2Enabled && !basicEnabled,
    });

    return (
      <div className={loginClasses}>
        {(samlEnabled || saml2Enabled) && this.renderSAMLAuth()}
        {basicEnabled && this.renderBasicAuth()}
        {this.renderLoginAnnouncement()}
      </div>
    );
  }

  /**
   * This function returns true false if user is using a supported browser
   * @returns {boolean}
   */
  isSupportedBrowser = () => {
    const browser = Bowser.getParser(window.navigator.userAgent);
    const isValidBrowser = browser.satisfies({
      safari: '>=13',
      'internet explorer': '>10',
      chrome: '>=83',
      firefox: '>=68',
      opera: '>=65',
      'microsoft edge': '>=83',
    });

    return isValidBrowser ?? false;
  };

  render() {
    const { dispatch, authenticated } = this.props;

    if (authenticated) {
      return (
        <Redirect
          to="/verification"
        />
      );
    }

    const didUserConsent = this.props.consented !== 'Yes';
    const logout = getQueryValue(window.location.search, 'logout');

    if (this.isSupportedBrowser()) {
      return (
        <div className="login eapp-core" id="login">
          {logout ? <Logout /> : <Consent dispatch={dispatch} />}
          <div
            id="seal-header"
            className="seal-header text-center"
            aria-hidden={didUserConsent}
            aria-disabled={didUserConsent}
          >
            <div className="content">
              <img src="/img/dcsa-logo.png" alt="National Background Investigation Services" />
              <h2>{i18n.t('login.title')}</h2>
            </div>
          </div>
          <div
            className="content"
            role="main"
            aria-hidden={didUserConsent}
            aria-disabled={didUserConsent}
          >
            {this.renderLogin()}
          </div>
        </div>
      );
    }
    return (
      <div className="usa-alert usa-alert-warning">
        <div className="usa-alert-body">
          <h3 className="usa-alert-heading">Your browser is not supported.</h3>
          <p className="usa-alert-text">
            In order to view the full content of this site, please use one of the following
            browsers at their latest versions: Safari, Firefox, Internet Explorer, Internet Edge,
            or Chrome.
          </p>
        </div>
      </div>
    );
  }
}

Login.propTypes = {
  authenticated: PropTypes.bool,
  history: PropTypes.object, // eslint-disable-line react/forbid-prop-types
  dispatch: PropTypes.func,
  errors: PropTypes.array, // eslint-disable-line react/forbid-prop-types
};

Login.defaultProps = {
  authenticated: false,
  history: {},
  dispatch: () => { },
  errors: null,
};

/**
 * Maps the relevant subtree state from the applications state tree.
 * In this case, we pull the authentication sub-state. This is mapped
 * to the authentication reducer. When actions are dispatched, this
 * method is executed which causes a re-render.
 */
function mapStateToProps(state) {
  const { authentication, application } = state;

  return {
    authenticated: authentication.authenticated,
    errors: authentication.error,
    consented: application.Settings.consented,
  };
}

// Wraps the App component with connect() which adds the dispatch()
// function to the props property for this component
export default withRouter(connect(mapStateToProps)(Login));
